'use client';

import classNames from 'classnames';
import { format, isAfter } from 'date-fns';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { PropsWithClassname } from '@/app/types';

import { ProjectNews } from '@/app/api';
import { CookiebotContext } from '@/app/components/CookiebotScript/CookiebotScript';
import ProjectNewsPost from '@/app/components/ProjectNewsPost/ProjectNewsPost';
import SubprojectBanner from '@/app/components/SubprojectBanner/SubprojectBanner';

import '@/app/components/ProjectStoryTab/projectStoryStyle.css';
import { slugify } from '@/app/utils/slugify/slugify';
import Link from 'next/link';
import { useIsUserInNewHeaderLayoutGroup } from '@/app/hooks/useIsUserInNewHeaderLayoutGroup';
import { useProjectData } from '@/app/providers/ProjectProvider';
import { useRemainingTimeText } from '@/app/components/ProjectTimeLeft/ProjectTimeLeft';

interface StoryTabText {
  label?: string;
  htmlContent?: string;
}

interface ProjectStoryTabProps extends PropsWithClassname {
  startedAt?: Date;
  projectNumericId: number;
  currentNewsPost?: ProjectNews;
  hasMoreNews: boolean;
  isSubprojectBannerVisible?: boolean;
  onAllNewsClick?: () => void;
  author?: string | null;
  texts: StoryTabText[];
  organizationAuthorSlug?: string;
  organizationAuthor?: string;
}

const EndsAtColumn = ({ endsAt }: { endsAt: Date }) => {
  const { timeRemainingText } = useRemainingTimeText(endsAt);

  const { t } = useTranslation();

  return (
    <div className="w-max whitespace-nowrap pr-[36px] md:pr-0">
      <div className="font-medium text-[14px] mb-[12px] leading-[24px] uppercase">
        {t('components.ProjectTimeLeft.tillCompletion')}
      </div>
      <div className="text-[14px]">{timeRemainingText}</div>
    </div>
  );
};

const ProjectStoryTab = ({
  projectNumericId,
  startedAt,
  currentNewsPost,
  hasMoreNews,
  isSubprojectBannerVisible,
  onAllNewsClick,
  author,
  texts,
  className,
  organizationAuthor,
  organizationAuthorSlug,
}: ProjectStoryTabProps) => {
  const { t } = useTranslation();
  const { areMarketingCookiesAllowed } = useContext(CookiebotContext);
  const isUserInNewHeaderLayoutGroup = useIsUserInNewHeaderLayoutGroup();
  const isAuthorVisible = !!author || !!organizationAuthor;
  const { project } = useProjectData();
  const scheduleEnding = project.timestamps.scheduledEnding;

  const shouldDisplayScheduleEnding =
    !!scheduleEnding && isUserInNewHeaderLayoutGroup && !isAfter(new Date(), new Date(scheduleEnding));

  return (
    <div className={classNames(className, '')}>
      <div
        className={classNames(
          'gap-[16px] overflow-x-auto grid',
          shouldDisplayScheduleEnding ? 'grid-cols-3' : 'grid-cols-2',
          {
            'md:justify-between': shouldDisplayScheduleEnding,
          },
        )}
      >
        {isAuthorVisible && (
          <div className={classNames('pr-[36px] md:pr-0')}>
            <div className="font-medium text-[14px] mb-[12px] leading-[24px] uppercase">
              {t('components.ProjectStoryTab.author')}
            </div>
            <div>
              {!!organizationAuthorSlug && (
                <Link className="text-primary-mid underline" href={`/${organizationAuthorSlug}`}>
                  {organizationAuthor}
                </Link>
              )}
              {!!organizationAuthor && !!author && <span>, </span>}
              {!!author && (
                <span className="text-[16px]" data-testid="person-author">
                  {author}
                </span>
              )}
            </div>
          </div>
        )}
        {shouldDisplayScheduleEnding && <EndsAtColumn endsAt={scheduleEnding} />}
        {!!startedAt && (
          <div className="w-max whitespace-nowrap">
            <div className="font-medium text-[14px] mb-[12px] leading-[24px] uppercase">
              {t('components.ProjectStoryTab.dateAdded')}
            </div>
            <div className="text-[14px]">{format(startedAt, t('dateFormat'))}</div>
          </div>
        )}
      </div>
      {currentNewsPost && (
        <div className="mt-[40px] lg:mt-[48px]">
          <span className="block text-[10px] lg:text-[12px] font-medium uppercase tracking-[3px] leading-[24px] lg:tracking-[4.5px] mb-[16px]">
            {t('components.ProjectStoryTab.lastNewsPost')}
          </span>
          <ProjectNewsPost newsPost={currentNewsPost} className="mb-[24px]" isCollapsed />
          {hasMoreNews && (
            <button
              className="block text-primary-mid hover:no-underline bg-transparent border-none mb-[32px]"
              onClick={onAllNewsClick}
            >
              {t('components.ProjectStoryTab.allNews')}
            </button>
          )}
        </div>
      )}
      <div className="project-story-text">
        {texts
          .filter((text) => Boolean(text.htmlContent))
          .map((text, index) => (
            <div key={index}>
              {!!text.label && (
                <h2
                  id={slugify(text.label)}
                  className="text-[24px] lg:text-[32px] leading-[32px] lg:leading-[40px] mt-[40px] mb-[20px] scroll-mt-[90px]"
                >
                  {text.label}
                </h2>
              )}
              <div
                className={classNames('project-story', {
                  'project-story-marketing-cookies-not-accepted': !areMarketingCookiesAllowed,
                  'project-story-marketing-cookies-accepted': areMarketingCookiesAllowed,
                })}
                dangerouslySetInnerHTML={{ __html: text.htmlContent! }}
              />
            </div>
          ))}
      </div>
      {isSubprojectBannerVisible && <SubprojectBanner projectNumericId={projectNumericId} className="mt-[40px]" />}
    </div>
  );
};

export default ProjectStoryTab;
